import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useAuthStore } from '../lib/store';
import { mockDreams } from '../lib/mockData';
import SublymWordmark from '../components/SublymWordmark';

export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLocked } = useAuthStore();

  // Magiclink : /?code=XXXX → redirect vers /access avec code pré-rempli
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      navigate(`/access?code=${encodeURIComponent(code)}`, { replace: true });
    }
  }, []);

  const handleStart = () => {
    if (!isAuthenticated) {
      navigate('/create');
    } else if (isLocked) {
      navigate('/lock');
    } else {
      // Go directly to the dream viewer with the first dream
      const firstDream = mockDreams[0];
      if (firstDream) {
        navigate(`/dream/${firstDream.id}`);
      } else {
        navigate('/dreams');
      }
    }
  };

  const heroImage = mockDreams[0]?.thumbnailUrl;

  return (
    <div className="min-h-screen flex flex-col safe-area-inset relative overflow-hidden">
      {/* Carbon base */}
      <div className="fixed inset-0 bg-system-carbon-500 -z-20" />

      {/* Ambient background image (very blurred) */}
      {heroImage && (
        <div
          className="fixed inset-0 -z-10 opacity-20"
          style={{
            backgroundImage: `url(${heroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(80px) saturate(120%)',
          }}
        />
      )}

      {/* Soft orbs — ivory, not colored */}
      <motion.div
        className="absolute top-20 -left-20 w-80 h-80 rounded-full -z-5"
        style={{ background: 'radial-gradient(circle, rgba(254,254,254,0.06) 0%, transparent 70%)' }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-32 -right-20 w-96 h-96 rounded-full -z-5"
        style={{ background: 'radial-gradient(circle, rgba(254,254,254,0.04) 0%, transparent 70%)' }}
        animate={{ scale: [1.15, 1, 1.15], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {/* SǛBLYM logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          className="flex flex-col items-center mb-8"
        >
          <SublymWordmark width={280} className="text-white" />
        </motion.div>

        {/* Tagline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center mb-6"
        >
          <p className="text-lg text-ivory-muted max-w-xs mx-auto leading-relaxed">
            Visualisez vos reves, manifestez votre realite
          </p>
        </motion.div>

        {/* Preview cards — glass effect */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="w-full max-w-sm mb-10"
        >
          <div className="flex gap-3 justify-center">
            {mockDreams.slice(0, 3).map((dream, i) => (
              <motion.div
                key={dream.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className="image-card w-24 h-36 flex-shrink-0"
              >
                <img src={dream.thumbnailUrl || ''} alt="" className="w-full h-full object-cover" />
                <div className="image-card-overlay" />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA — glass ivoire button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <button onClick={handleStart} className="btn-primary flex items-center gap-3 px-10 py-4 text-lg">
            <span>Commencer</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </motion.div>
      </div>

      {/* Version */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.8 }}
        className="text-center text-sm text-ivory-faint pb-6"
      >
        v1.0 — Dream Mode
      </motion.p>
    </div>
  );
}
